import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { COMBO_DEFS } from '../data/comboDefs';
import { getProductImageUrl } from '../utils/productImage';
import { useBreakpoint } from '../hooks/useBreakpoint';
import api from '../api/axios';

const C = { primary: '#8B1A1A', gold: '#D4A017' };

function ComboCard({ combo, products, loading }) {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const total = products.reduce(
    (sum, p) => sum + Math.round(parseFloat(p.effective_price || p.price)),
    0
  );
  const savings = Math.round(total * 0.2);
  const comboPrice = total - savings;

  const images = products.slice(0, 3).map((p) =>
    getProductImageUrl(p, `https://placehold.co/200x240/8B1A1A/gold?text=${encodeURIComponent(p.name.slice(0, 10))}`)
  );

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.09)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        border: `1px solid #f0e0e0`,
      }}
    >
      <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 2 }}>
        <span style={{ background: combo.badgeColor, color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12 }}>
          {combo.badge}
        </span>
      </div>

      {/* Product image collage */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', height: isMobile ? 140 : 180, gap: 2, background: '#f8f0e8' }}>
        {loading
          ? [0, 1, 2].map(i => <div key={i} style={{ background: '#f0e0d0', animation: 'pulse 1.5s infinite' }} />)
          : images.map((src, i) => (
            <img
              key={i}
              src={src}
              alt={combo.categoryLabels[i]}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={e => { e.target.src = `https://placehold.co/200x240/8B1A1A/gold?text=A1`; }}
            />
          ))}
      </div>

      <div style={{ padding: isMobile ? '14px' : '18px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <p style={{ fontSize: 11, color: '#999', margin: 0, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {combo.occasion}
        </p>
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 17 : 20, color: '#2C2C2C', margin: 0, lineHeight: 1.3 }}>
          {combo.name}
        </h3>
        <p style={{ fontSize: 13, color: '#777', margin: 0 }}>{combo.tagline}</p>

        {/* Items included */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {combo.categoryLabels.map(label => (
            <span key={label} style={{ background: '#FFF8F0', border: '1px solid #e0d0d0', fontSize: 11, color: '#666', padding: '2px 8px', borderRadius: 10 }}>
              {label}
            </span>
          ))}
        </div>

        {/* Pricing */}
        {!loading && products.length === combo.categories.length && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: C.primary }}>
              ₹{comboPrice.toLocaleString('en-IN')}
            </span>
            <span style={{ fontSize: 14, color: '#999', textDecoration: 'line-through' }}>
              ₹{total.toLocaleString('en-IN')}
            </span>
            <span style={{ fontSize: 12, background: '#e8f5e9', color: '#2e7d32', fontWeight: 700, padding: '2px 7px', borderRadius: 3 }}>
              Save ₹{savings.toLocaleString('en-IN')}
            </span>
          </div>
        )}

        <button
          onClick={() => navigate(`/combo/${combo.slug}`)}
          style={{
            marginTop: 'auto',
            padding: '11px 0',
            background: combo.accent,
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            letterSpacing: 0.4,
          }}
        >
          View Bundle →
        </button>
      </div>
    </div>
  );
}

export default function ComboSection() {
  const { isMobile } = useBreakpoint();
  const [comboProducts, setComboProducts] = useState(
    COMBO_DEFS.reduce((acc, c) => { acc[c.slug] = []; return acc; }, {})
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetches = COMBO_DEFS.flatMap(combo =>
      combo.categories.map(cat =>
        api.get(`/products/?category=${cat}&page_size=1`)
          .then(res => ({ slug: combo.slug, product: (res.data.results || res.data)[0] }))
          .catch(() => null)
      )
    );

    Promise.all(fetches).then(results => {
      const map = COMBO_DEFS.reduce((acc, c) => { acc[c.slug] = []; return acc; }, {});
      results.forEach(r => {
        if (r?.product) map[r.slug].push(r.product);
      });
      setComboProducts(map);
      setLoading(false);
    });
  }, []);

  const anyLoaded = COMBO_DEFS.some(c => (comboProducts[c.slug] || []).length > 0);
  if (!loading && !anyLoaded) return null;

  return (
    <section style={{ padding: isMobile ? '40px 16px' : '60px 24px', maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: isMobile ? 24 : 32, color: '#2C2C2C', textAlign: 'center', marginBottom: 8 }}>
        Festive & Wedding Combos
      </h2>
      <p style={{ textAlign: 'center', color: '#888', marginBottom: isMobile ? 24 : 40, fontSize: 15 }}>
        Curated bundles — styled together, priced smarter
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? 16 : 28 }}>
        {COMBO_DEFS.map(combo => (
          <ComboCard
            key={combo.slug}
            combo={combo}
            products={comboProducts[combo.slug] || []}
            loading={loading}
          />
        ))}
      </div>
    </section>
  );
}
